import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  applyGenerationDetail,
  applyRetryConfirmed,
  applyRetryStarted,
  fetchGeneration,
  generationKeys,
  type GenerationDetail,
  type GenerationItem,
} from '@entities/Generation';
import { planKeys, type Quota } from '@entities/Plan';
import { regenerateCardWithGenerations, RegenerateCardError } from '@features/CardsWorkspace';
import { trackEvent } from '@shared/analytics';
import { retryFailure, type RetryFailure } from '../lib/retryError';

/** Исход «Повторить» неизвестен (нет ответа, 5xx): POST не повторяем, а через 3 с перечитываем состав. */
const UNKNOWN_RECHECK_MS = 3000;

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/**
 * «Повторить · 1 жетон» у упавшего элемента записи (ТЗ главной § 22.6): `POST /cards/{id}/regenerate` —
 * тот же card_id и тот же выбор, 1 жетон, запись та же (не DELETE + POST: тогда осталась бы «удалена»,
 * а нажатие дало бы вторую запись). Успех — элемент сразу «генерируется», запись «генерируем», квота
 * перечитывается, генерация — в слежении как новая попытка. Неудача — `failure` (`lib/retryError.ts`):
 * строка под элементом, «Добавить вручную» вместо «Повторить» у слова не из словаря, скрытое «Жетоны
 * закончились» у своего 429. Живёт, пока строка элемента на экране: свернули запись — снова «Повторить».
 */
export function useRetryItem(detail: GenerationDetail, item: GenerationItem, surface: 'desktop' | 'phone') {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<RetryFailure | null>(null);
  // Второе нажатие до перерисовки не уходит вторым запросом (второй жетон).
  const inFlight = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(async () => {
    const cardId = item.cardId;
    const moduleId = detail.module?.id;
    if (inFlight.current || !cardId || !moduleId) return;
    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    trackEvent('generation_action', { surface, action: 'retry', kind: detail.kind });
    // Строка элемента могла уйти (запись свернули) — кэш и слежение обновляем всё равно.
    const settle = (next: RetryFailure | null) => {
      inFlight.current = false;
      if (!alive.current) return;
      setBusy(false);
      setFailure(next);
    };
    const refreshPlan = () => {
      void qc.invalidateQueries({ queryKey: planKeys.quota });
      void qc.invalidateQueries({ queryKey: planKeys.billing });
    };
    const reloadDetail = () => void qc.invalidateQueries({ queryKey: generationKeys.byId(detail.id), exact: true });
    try {
      const { generations } = await regenerateCardWithGenerations(cardId, moduleId);
      // Сводки записей — из ответа повтора (контракт § 7.1); старый бэкенд их не отдаёт — расчёт.
      applyRetryStarted(qc, detail.id, item.id, cardId, generations);
      refreshPlan();
      settle(null);
    } catch (err) {
      const code = err instanceof RegenerateCardError ? err.code : 'UNKNOWN';
      switch (code) {
        case 'NETWORK':
        case 'SERVER': {
          await wait(UNKNOWN_RECHECK_MS);
          let fresh: GenerationDetail | null = null;
          const requestedAt = Date.now();
          try {
            fresh = await fetchGeneration(detail.id);
          } catch {
            fresh = null;
          }
          const again = fresh?.items.find((x) => x.id === item.id);
          if (fresh && (again?.status === 'generating' || again?.status === 'extending')) {
            // Запуск всё же прошёл — это успех.
            applyRetryConfirmed(qc, fresh, cardId, requestedAt);
            refreshPlan();
            settle(null);
            return;
          }
          if (fresh) applyGenerationDetail(qc, fresh, requestedAt);
          settle(retryFailure('UNKNOWN', false));
          return;
        }
        case 'DAILY_LIMIT_REACHED': {
          // Сервер сказал: жетонов нет. Остаток в кэше квоты — 0, кнопка сразу неактивная «Жетоны закончились»
          // (перечитка его уточнит); строкой под элементом то же второй раз не пишем. Квоты в кэше нет — строка
          // остаётся, пока кнопка не станет такой же.
          const known = qc.getQueryData<Quota>(planKeys.quota);
          if (known) qc.setQueryData<Quota>(planKeys.quota, { ...known, remaining: 0 });
          refreshPlan();
          settle(retryFailure(code, !!known));
          return;
        }
        case 'NOT_IN_DICTIONARY':
          // Словарный гейт до резерва: жетон не списан, карточка не менялась — квоту и состав не перечитываем.
          settle(retryFailure(code, false));
          return;
        case 'CARD_NOT_FOUND':
        case 'CARD_STATE_NOT_FOUND':
        case 'CARD_NOT_REGENERATABLE':
          reloadDetail();
          settle(retryFailure(code, false));
          return;
        default:
          settle(retryFailure(code, false));
      }
    }
  }, [detail, item, qc, surface]);

  /** Убрать прошлый исход: квота перечиталась, а жетоны есть — скрытая строка о своём 429 больше не верна. */
  const clear = useCallback(() => setFailure(null), []);

  return { busy, failure, run, clear };
}
